import axios, { InternalAxiosRequestConfig } from 'axios';
import { InvalidTokenError } from 'jwt-decode';
import { refreshToken, updateSmgTokens } from '../api/authApi';
import { isTokenExpired } from './jwt-helper';
import { reloadPage } from './page-helper';
import * as storageHelper from './storage-helper';

// Requests that start while a refresh is running wait for that refresh instead of starting their own. Parallel
// refreshes with the same refresh token fail when Keycloak revokes used refresh tokens (TEAM-1883).
let refreshInFlight: Promise<string> | null = null;

// A refreshed pair that could not be stored in the backend is retried before the next request. With refresh token
// rotation the backend's copy would otherwise be dead, and its next refresh would clear the whole session.
let backendSyncPending = false;
let backendSyncInFlight: Promise<void> | null = null;

async function storeTokensInBackend(): Promise<void> {
  try {
    await updateSmgTokens();
    backendSyncPending = false;
  } catch (error) {
    backendSyncPending = true;
    console.warn('axios-interceptors: could not store the refreshed SMG tokens in the backend, will retry', error);
  }
}

function syncPendingTokens(): Promise<void> {
  if (!backendSyncInFlight) {
    backendSyncInFlight = storeTokensInBackend().finally(() => {
      backendSyncInFlight = null;
    });
  }

  return backendSyncInFlight;
}

function refreshAccessToken(accessToken: string): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const response = await refreshToken(accessToken, storageHelper.getSmgRefreshToken());
        storageHelper.setSmgToken(response.data);
        await storeTokensInBackend();

        return response.data.accessToken;
      } catch (error) {
        // Handled here, inside the shared refresh, so the session ends once however many requests are waiting.
        if (isRefreshTokenRejected(error)) {
          endSession();
        }

        throw error;
      } finally {
        refreshInFlight = null;
      }
    })();
  }

  return refreshInFlight;
}

// Keycloak answers 400 or 401 for a dead refresh token and names the reason "invalid_grant" in the body. The same
// statuses with another reason (invalid_client, unauthorized_client, invalid_request) are configuration errors, and
// network errors or 5xx are outages: none of those may destroy the stored session.
function isRefreshTokenRejected(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.code === 'ERR_NETWORK') {
    return false;
  }

  const reason = (error.response?.data as { error?: unknown } | undefined)?.error;
  if (reason === 'invalid_grant') {
    return true;
  }

  const status = error.response?.status;

  return (status === 400 || status === 401) && !reason;
}

// The SMG session is dead, so an SMG logout with the refresh token cannot succeed either. Reloading sends the tab
// back through SSO, where checkAuthentication restores the session from the backend or shows the login page.
function endSession() {
  if (!storageHelper.getSmgAccessToken() && !storageHelper.getSmgRefreshToken()) {
    return; // another request already ended it
  }

  storageHelper.removeSmgToken();
  reloadPage();
}

const registerInterceptors = () => {
  axios.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (config.url?.includes('/login/')) {
        console.warn('REQUEST::::::::::::' + config.url);
        return config;
      }

      if (config.url?.endsWith('/auth/logout')) {
        // The backend logs out by Teams identity only. Sending an expired SMG bearer would be rejected, and refreshing it
        // first could end the session and reload the tab before the logout ever reaches the backend (TEAM-1883).
        return config;
      }

      console.log('REQUEST::::::::::::' + config.url);
      let accessToken = storageHelper.getSmgAccessToken();

      if (!accessToken) {
        return config;
      }

      try {
        if (isTokenExpired(accessToken)) {
          // The refresh stores the new pair itself; a failed store is retried by a later request below.
          accessToken = await refreshAccessToken(accessToken);
        } else if (backendSyncPending && !config.url?.endsWith('/auth/update-smg-tokens')) {
          await syncPendingTokens();
        }
      } catch (error) {
        console.log(error);
        if (error instanceof InvalidTokenError) {
          // A cookie that no longer decodes can never be refreshed; the restore flow replaces it after the reload.
          endSession();
        }

        return Promise.reject(error);
      }

      config.headers.Authorization = `Bearer ${accessToken}`;
      config.headers['Content-Type'] = 'application/json';

      return config;
    },
    function (error) {
      console.log(`MSTeamsConnectError: ${error}`);
      reloadPage();

      return Promise.reject(error);
    }
  );
};

// Test hook: the module keeps refresh and sync state between requests.
export const resetInterceptorState = () => {
  refreshInFlight = null;
  backendSyncPending = false;
  backendSyncInFlight = null;
};

export default registerInterceptors;
